/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useLayoutEffect, useRef, useState } from 'react';

import { VodItemType } from 'client-code/vod-browse';

import { BrandingUiInfoKeys } from '../../../core/entities';
import { NdvrInteractor } from '../../../core/interactors';
import { ImageUrlGenerator } from '../../../core/utils';
import { ProgressBar } from '../../common/ProgressBar';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';

import './GalleryItem.scss';

interface GalleryItemProps {
    objectId: string;
    type: string;
    isHighlighted?: boolean;
    percentWatched?: number | null | undefined;
    isGhostModeEnabled?: boolean;
    title: string;
}
const MOVIE_POSTER = { HEIGHT: 252, WIDTH: 168 };

const GalleryItem: FunctionComponent<GalleryItemProps> = ({
    objectId,
    type,
    isHighlighted,
    percentWatched,
    isGhostModeEnabled,
    title,
}) => {
    const [isImageLoaded, setIsImageLoaded] = useState(true);
    const [isOverflowed, setIsOverflowed] = useState<boolean>(false);
    const titleRef = useRef<HTMLDivElement>(null);

    const imageUrl = new ImageUrlGenerator();
    const services = new NdvrInteractor();
    const DEFAULT_IMAGE = services.getAppUiImageByKey(
        BrandingUiInfoKeys.APP_ICONS.DEFAULT_BACKGROUND,
    );

    let imageType;
    switch (type.toLowerCase()) {
        case VodItemType.MovieVodItem.toLowerCase():
        case VodItemType.SpecialVodItem.toLowerCase():
            imageType = ImageLoaderTypes.GALLERY_MOVIE_VOD_ITEM;
            break;

        case VodItemType.EpisodeVodItem.toLowerCase():
        case VodItemType.UnifiedCollectionVodItem.toLowerCase():
        case VodItemType.FolderVodItem.toLowerCase():
            imageType = ImageLoaderTypes.GALLERY_EPISODE_VOD_ITEM;
            break;
        default:
    }

    const ImageUrl = imageUrl.buildImageUrl(
        services.getServicesBaseUrls()['image-host'],
        objectId,
        type === VodItemType.MovieVodItem ? 'MOVIE' : 'GALLERY_TILE',
        type === VodItemType.MovieVodItem ? MOVIE_POSTER.WIDTH : undefined,
        type === VodItemType.MovieVodItem ? MOVIE_POSTER.HEIGHT : undefined,
    );

    const validVodItemTypesForProgressBar = [
        VodItemType.EpisodeVodItem.toLowerCase(),
        VodItemType.SpecialVodItem.toLowerCase(),
        VodItemType.MovieVodItem.toLowerCase(),
    ];

    /**
     * Determines if the given type (Episode / Movie / Special) is valid for a VOD item type
     * and if the progress bar should be displayed.
     */
    const isTypeValidForVodItemType = validVodItemTypesForProgressBar.includes(type.toLowerCase());

    const getHighlightClass = () => {
        if (isHighlighted && isGhostModeEnabled) return 'gallery-grid-ghost-highlight';
        if (isHighlighted) return 'gallery-grid-highlight';
        return 'gallery-grid-normal';
    };

    useLayoutEffect(() => {
        const gridTitle = titleRef.current;
        if (gridTitle) {
            const isOverflowing = gridTitle.scrollHeight - gridTitle.clientHeight > 1;
            setIsOverflowed(isOverflowing);
        }
    }, [title]);

    return (
        <React.Fragment>
            <div className={getHighlightClass()}>
                <div className={`gallery-item`}>
                    <ImageLoader
                        src={ImageUrl || DEFAULT_IMAGE?.imageUrl}
                        imageType={imageType}
                        handleOnImageLoaded={(imageLoadedSuccessfully: boolean) => {
                            setIsImageLoaded(imageLoadedSuccessfully);
                        }}
                    />
                    <div
                        ref={titleRef}
                        className={`gallery-item-title ${isOverflowed ? 'fade' : ''}`}
                        style={{
                            visibility: !isImageLoaded ? 'visible' : 'hidden',
                        }}
                    >
                        {title}
                    </div>
                    {isTypeValidForVodItemType && (
                        <div className="gallery-item-progress-bar">
                            <ProgressBar percentage={percentWatched ?? undefined} />
                        </div>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default GalleryItem;
